import { DestroyRef, Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DynamicPricingStore } from '@app/features/pricing-and-availability/services/dynamic-pricing/dynamic-pricing.store';
import { BillingFacade } from '@app/shared/+state/billing';
import { UsersFacade } from '@app/shared/+state/users';
import {
  DYNAMIC_PRICING_ACTIVATE_PERMISSIONS,
  DynamicPricingOptionsResponse,
} from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { EventsService } from '@app/shared/services/events/events.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { sleep } from '@app/shared/utils';
import { LoadingState } from '@ui';
import { BehaviorSubject, Observable, combineLatest, filter, map, of, startWith, switchMap, take } from 'rxjs';

interface DynamicPricingServiceState {
  $config: WritableSignal<DynamicPricingOptionsResponse | null>;
  $configLoading: WritableSignal<LoadingState>;
  $propertyId: WritableSignal<number | null>;
}

type StateKey = keyof DynamicPricingServiceState;

export const PREVIEW_DYNAMIC_PRICING_QUERY_PARAM = 'preview-dynamic-pricing';
export const PREVIEW_DYNAMIC_PRICING_STEP_QUERY_PARAM = 'step';

@Injectable({
  providedIn: 'root',
})
export class DynamicPricingService {
  // Dependencies
  private pusherService: PusherService = inject(PusherService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private propertiesService: PropertiesService = inject(PropertiesService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private dynamicServiceStore: DynamicPricingStore = inject(DynamicPricingStore);
  private segmentIoService: SegmentIoService = inject(SegmentIoService);

  // State signals
  private state: DynamicPricingServiceState = {
    $config: signal<DynamicPricingOptionsResponse | null>(null),
    $configLoading: signal(LoadingState.NotSent),
    $propertyId: signal<number | null>(null),
  };

  // Selectors
  public $config = computed(() => this.state.$config());
  public $dynamicPricingOn = computed(() => this.state.$config()?.outbound_dynamic_pricing_enabled);
  public $dynamicPricingErrors = computed(() => this.state.$config()?.data_errors);
  public $dynamicPricingOutOfMarket = computed(() => !this.state.$config()?.in_market);
  public $configLoading = computed(() => this.state.$configLoading());
  public $propertyId = computed(() => this.state.$propertyId());
  public $previewMode: WritableSignal<'price' | 'min-stay'> = signal<'price' | 'min-stay'>('price');

  // Observables for state
  private enableOutboundDynamicPricingLoading$ = new BehaviorSubject<LoadingState>(LoadingState.NotSent);
  public dynamicPricingPusherUpdates$ = new BehaviorSubject<boolean>(false);
  public enableDynamicPricingProgress$ = this.enableOutboundDynamicPricingLoading$.asObservable().pipe(
    switchMap((loading) => {
      if (loading !== LoadingState.Success) {
        return of(1);
      }

      return this.dynamicPricingPusherUpdates$.pipe(
        filter((updated) => updated),
        take(1),
        map(() => 2)
      );
    })
  );
  public previewVisible$ = new BehaviorSubject<boolean>(false);

  // Observables for feature permissions
  private isPrimaryUser = this.permissionsService.isPrimaryUser();
  private embeddedPricingEntitlement$ = this.usersFacade.embeddedPricingEntitlement$.pipe(
    map((entitlement) => {
      return entitlement?.available;
    })
  );
  public dynamicPricingActivatePermissions$: Observable<DYNAMIC_PRICING_ACTIVATE_PERMISSIONS> = combineLatest([
    this.billingFacade.isTrialSubscription$,
    this.embeddedPricingEntitlement$,
    this.isPrimaryUser,
    this.usersFacade.isInternalHospitableHost$,
  ]).pipe(
    map(([isTrial, embeddedPricingEntitlement, isPrimaryUser, isInternalHospitableHost]) => {
      // Whether or not the account has the entitlement, a secondary user cannot enable dynamic pricing (or go to the billing page)
      if (!isPrimaryUser) {
        return DYNAMIC_PRICING_ACTIVATE_PERMISSIONS.ADMIN_ACCOUNT_REQUIRED;
      }

      // If the account does not have the entitlement, a plan upgrade is required
      if (!embeddedPricingEntitlement) {
        return DYNAMIC_PRICING_ACTIVATE_PERMISSIONS.PLAN_UPGRADE_REQUIRED;
      }

      // If the user is an internal Hospitable host, we'll ignore the trial status
      // (Hospitable hosts are all in trial mode for accounting purposes)
      if (isInternalHospitableHost) {
        return DYNAMIC_PRICING_ACTIVATE_PERMISSIONS.AVAILABLE;
      }

      // If the account is in trial, the user needs to update their payment method
      if (isTrial) {
        return DYNAMIC_PRICING_ACTIVATE_PERMISSIONS.BILLING_UPDATE_REQUIRED;
      }

      return DYNAMIC_PRICING_ACTIVATE_PERMISSIONS.AVAILABLE;
    }),
    filter((permission) => permission !== undefined)
  );

  public dynamicPricingSidebarSubtitle$: Observable<string> = this.dynamicPricingActivatePermissions$.pipe(
    map((permissions) => {
      return permissions === DYNAMIC_PRICING_ACTIVATE_PERMISSIONS.AVAILABLE
        ? 'Included in your plan'
        : 'Included with Host, Pro and Mogul plans';
    }),
    startWith('Included with Host, Pro and Mogul plans')
  );

  private config$ = toObservable(this.$config);

  /**
   * Initialize the dynamic pricing service for a property
   * @param propertyId - The ID of the property to initialize the service for
   */
  public init(propertyId: number) {
    const state = this.dynamicServiceStore.getPropertyState(propertyId);
    if (state()?.config) {
      this.state.$config.set(state().config as DynamicPricingOptionsResponse);
      this.state.$configLoading.set(state().configLoading as LoadingState);
      this.state.$propertyId.set(propertyId);
    } else {
      this.resetState();
    }

    this.updateState('$propertyId', propertyId);
    this.loadDynamicPricingConfig();
    this.pusherService
      .getDynamicPricingRecommendationsUpdate(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadDynamicPricingConfig();
        this.dynamicPricingPusherUpdates$.next(true);
      });
    this.subscriptionCheck();

    // Landing on the page with a query param will automatically display the dynamic pricing preview and options
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        filter((params) => params[PREVIEW_DYNAMIC_PRICING_QUERY_PARAM] === 'true'),
        switchMap(() => this.config$.pipe(filter((config) => Boolean(config)))),
        take(1)
      )
      .subscribe((config) => {
        this.state.$config.set(config);
        this.eventsService.fire(`dynamic-pricing-sidebar-view-force-${this.$propertyId()}`, 'dynamic-pricing');
      });
  }

  // decouple this in case we need to pre-emeptively call it
  public subscriptionCheck() {
    this.isPrimaryUser
      .pipe(
        filter((isPrimaryUser) => isPrimaryUser),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.billingFacade.loadSubscriptionIfNotLoaded();
      });
  }

  public resetState() {
    this.previewVisible$.next(false);
    this.updateState('$propertyId', null);
    this.updateState('$configLoading', LoadingState.NotSent);
    this.updateState('$config', null);
  }

  public loadDynamicPricingConfig() {
    this.updateState('$configLoading', LoadingState.Pending);
    this.propertiesService.getDynamicPricingOptions(this.$propertyId()).subscribe((data) => {
      this.updateState('$config', data as DynamicPricingOptionsResponse);
      this.updateState('$configLoading', LoadingState.Success);
    });
  }

  // TODO: this just updates the state for now
  public setDynamicPricingConfig(payload: DynamicPricingOptionsResponse) {
    this.updateState('$config', payload);
  }

  public activateOutboundDynamicPricing() {
    this.enableOutboundDynamicPricingLoading$.next(LoadingState.Pending);
  }

  public activateOutboundDynamicPricingSuccess() {
    this.enableOutboundDynamicPricingLoading$.next(LoadingState.Success);
    this.segmentIoService.track(SegmentEvent.DynamicPricingEnabled, {
      propertyId: this.$propertyId(),
    });
  }

  /**
   * Updates a specific piece of state
   * @param key - The state key to update
   * @param value - The new value
   */
  private updateState(key: StateKey, value: unknown) {
    const signal = this.state[key];
    if (key === '$config' && value !== null) {
      const currentValue = signal() || {};
      // @ts-expect-error - Dynamic typing of value is tricky here
      signal.set({ ...currentValue, ...value });
    } else {
      signal.set(value as never);
    }
    this.updateStore(this.$propertyId(), key, value);
  }

  private updateStore(propertyId: number, key: StateKey, value: unknown) {
    if (key === '$config') {
      this.dynamicServiceStore.updateState(propertyId, 'config', value);
    } else if (key === '$configLoading') {
      this.dynamicServiceStore.updateState(propertyId, 'configLoading', value);
    }
  }

  public clearEvents(propertyId: number) {
    // clear navigation-related events, attached full list though
    this.eventsService.clearEvents([
      // `dynamic-pricing-disabled-${propertyId}`,
      // `dynamic-pricing-enabled-${propertyId}`,
      `dynamic-pricing-preview-fetch-${propertyId}`,
      // `dynamic-pricing-preview-loading-${propertyId}`,
      // `dynamic-pricing-property-error-modal-${propertyId}`,
      `dynamic-pricing-sidebar-view-force-${propertyId}`,
      // `dynamic-pricing-updated-${propertyId}`,
    ]);
  }

  public toggleVisibility(visibility: boolean) {
    if (typeof visibility === 'undefined') {
      visibility = !this.previewVisible$.value;
    }
    this.previewVisible$.next(visibility);
  }

  public async setPreviewMode(mode: 'price' | 'min-stay') {
    if (this.$previewMode() !== mode) {
      this.eventsService.fire(`dynamic-pricing-preview-loading-${this.$propertyId()}`, true);
      this.$previewMode.set(mode);
      await sleep(1000);
      this.eventsService.fire(`dynamic-pricing-preview-loading-${this.$propertyId()}`, false);
    }
  }

  constructor(
    private usersFacade: UsersFacade,
    private permissionsService: UserPermissionsService,
    private billingFacade: BillingFacade,
    private eventsService: EventsService
  ) {}
}
